<template>
  <div class="ev-simulate-page">
    <div class="page-header">
      <div class="header-main">
        <!-- 推导 -->
        <div class="header-block derive-block">
          <div class="block-label">推导</div>
          <el-form size="mini" :inline="true" class="compact-form">
            <el-form-item label="开始日期">
              <el-date-picker v-model="param.startDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期"
                style="width: 140px" :disabled="running"></el-date-picker>
            </el-form-item>
            <el-form-item label="天数">
              <el-input-number v-model="param.days" :min="1" :max="7" :controls="false" style="width: 48px"
                :disabled="running"></el-input-number>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" size="mini" :loading="running" @click="startSimulate">开始推导</el-button>
            </el-form-item>
            <el-button size="mini" @click="goConfig">配置</el-button>
          </el-form>
        </div>

        <!-- 求解 EV 概要（居中突出） -->
        <el-tooltip v-if="hasSolveMetrics" content="净值 = 收工EV − 缺票惩罚 − D+1惩罚 − 行程惩罚（行程惩罚 = Σ 每趟 trip × 路线积分）"
          placement="bottom">

          <div class="header-block ev-block">
            <div class="ev-metric">
              <span class="ev-metric-label">收工EV（全局）</span>
              <span class="ev-metric-value">{{ formatEvNum(globalStopWorkEv) }}</span>
            </div>
            <div class="ev-metric" v-if="filterPathId">
              <span class="ev-metric-label">收工EV（本区域）</span>
              <span class="ev-metric-value muted">{{ formatEvNum(areaStopWorkEv) }}</span>
            </div>
            <div class="ev-metric is-penalty">
              <span class="ev-metric-label">缺票惩罚</span>
              <span class="ev-metric-value">{{ formatEvNum(slackPenaltyValue) }}</span>
            </div>
            <div class="ev-metric is-penalty">
              <span class="ev-metric-label">D+1惩罚</span>
              <span class="ev-metric-value">{{ formatEvNum(penaltyValue) }}</span>
            </div>
            <div class="ev-metric is-penalty">
              <span class="ev-metric-label">调位惩罚</span>
              <span class="ev-metric-value">{{ formatEvNum(tripPenaltyValue) }}</span>
            </div>
            <div class="ev-metric is-gain" v-if="repositionGainValue != null">
              <span class="ev-metric-label">调位收益</span>
              <span class="ev-metric-value">{{ formatEvNum(repositionGainValue) }}</span>
            </div>
            <div class="ev-metric is-objective">
              <span class="ev-metric-label">最终EV（净值）</span>
              <span class="ev-metric-value">{{ formatEvNum(objectiveValue) }}</span>
            </div>
          </div>
        </el-tooltip>
        <div class="header-block ev-block ev-placeholder" v-else>
          <span class="ev-placeholder-text">完成 EV 推导后在此显示求解概要</span>
        </div>

        <!-- 查看筛选 -->
        <div class="header-block view-block">
          <div class="block-label">查看</div>
          <el-form size="mini" :inline="true" class="compact-form">
            <el-form-item label="路线">
              <el-select v-model="filterPathId" placeholder="全部路线" clearable style="width: 120px">
                <el-option v-for="item in pathOpts" :key="item.id" :label="item.name" :value="item.id"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="日期">
              <el-date-picker v-model="viewDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期"
                style="width: 140px"></el-date-picker>
            </el-form-item>
            <el-form-item>
              <el-button size="mini" :loading="refreshing" @click="onRefresh">刷新</el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <!-- 账本统计 + 推导进度 -->
      <div class="header-sub">
        <div class="plan-stats" v-if="dayView">
          <span class="stat-tag">账本 {{ solveStatus }}</span>
          <span class="stat-tag">已派 {{ filteredAssignedPerson }} 人</span>
          <span class="stat-tag">未派 {{ filteredUnassignedPerson }} 人</span>
          <span class="stat-tag">行程 {{ filteredTripCount }} 个</span>
          <span class="stat-tag stat-tag--time" v-if="solveExecutedAt">
            推导于 {{ solveExecutedAt | dateFormat('yyyy-MM-dd hh:mm:ss') }}
            <template v-if="solveOperator">（{{ solveOperator }}）</template>
          </span>
        </div>
        <div class="task-status">
          <div class="toolbar__progress" v-if="taskId && task && task.status === 0">
            <span class="status-text">{{ progressText }}</span>
            <el-progress :percentage="progressPercent" :stroke-width="6" :show-text="false"
              class="inline-progress"></el-progress>
            <span class="progress-pct">{{ progressPercent }}%</span>
          </div>
          <span class="toolbar__error" v-else-if="task && task.status === 2">{{ task.errorMsg }}</span>
          <span class="toolbar__done" v-else-if="taskId && task && task.status === 1">
            推导完成
            <template v-if="task.completeTime">于 {{ task.completeTime | dateFormat('yyyy-MM-dd hh:mm:ss') }}</template>
          </span>
        </div>
      </div>
    </div>

    <div class="main-content">
      <el-tabs v-model="tab" class="fill-tabs">
        <el-tab-pane name="unassigned">
          <span slot="label">已订（{{ filteredUnassignedPerson }}人）</span>
          <unassigned-order ref="unassignedRef" :list="filteredUnassignedOrders" :date="viewDate"
            :table-height="tableMaxHeight" />
        </el-tab-pane>

        <el-tab-pane name="dispatched">
          <span slot="label">已派（{{ filteredAssignedPerson }}人）</span>
          <simulated-dispatched-order ref="dispatchedRef" :trips="filteredDispatchedTrips"
            :table-height="tableMaxHeight" />
        </el-tab-pane>

        <el-tab-pane name="idle">
          <span slot="label">可售余票</span>
          <idle-vehicle-panel v-if="filterPathId" ref="idleRef" :rows="sellableSnapshots" :table-height="tableMaxHeight"
            :loading="refreshing" />
          <div v-else class="idle-empty-hint">请选择路线后点击刷新查看可售余票</div>
        </el-tab-pane>

        <el-tab-pane name="opAreaHour">
          <span slot="label">运营指标</span>
          <op-area-hour-panel v-if="filterPathId" :rows="opAreaHourSnapshots" :table-height="tableMaxHeight"
            :loading="refreshing" />
          <div v-else class="idle-empty-hint">请选择路线后点击刷新查看运营层数据</div>
        </el-tab-pane>

        <el-tab-pane name="startVehicleTip">
          <span slot="label">日初状态（{{ startVehicleTipCount }}人）</span>
          <vehicle-tip-panel :rows="startVehicleTipRows" :view-date="viewDate"
            :table-height="tableMaxHeight" :loading="refreshing" />
        </el-tab-pane>

        <el-tab-pane name="vehicleTip">
          <span slot="label">日末状态（{{ vehicleTipCount }}人）</span>
          <vehicle-tip-panel :rows="vehicleTipRows" :view-date="viewDate"
            :table-height="tableMaxHeight" :loading="refreshing" />
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import {
  submitEvSimulate,
  getEvSimulateTask,
  getEvPlanView,
  getEvSellableView,
  getEvOpAreaHourView,
  getEvSolveSummaryView,
  getEvVehicleTipView,
  getEvStartVehicleTipView
} from "@/api/ev_solver";
import { DateUtil } from "@/util";
import { mapState } from "vuex";
import UnassignedOrder from "./UnassignedOrder";
import SimulatedDispatchedOrder from "./SimulatedDispatchedOrder";
import IdleVehiclePanel from "./IdleVehiclePanel";
import OpAreaHourPanel from "./OpAreaHourPanel";
import VehicleTipPanel from "./VehicleTipPanel";

/** 各页签数据缓存，切换页签/筛选条件时只读缓存，不自动请求 */
function createViewCache() {
  return {
    plan: {},
    sellable: {},
    opAreaHour: {},
    vehicleTip: {},
    startVehicleTip: {},
    solveSummary: {}
  };
}

export default {
  components: {
    UnassignedOrder,
    SimulatedDispatchedOrder,
    IdleVehiclePanel,
    OpAreaHourPanel,
    VehicleTipPanel
  },

  data() {
    return {
      tab: "dispatched",
      viewDate: DateUtil.getDayStr(0, "yyyy-MM-dd"),
      filterPathId: null,
      dayView: null,
      sellableSnapshots: [],
      opAreaHourSnapshots: [],
      solveSummary: null,
      viewCache: createViewCache(),
      refreshing: false,
      lastLoadedQueryKey: null,
      param: {
        startDate: DateUtil.getDayStr(0, "yyyy-MM-dd"),
        days: 3
      },
      taskId: null,
      task: null,
      running: false,
      pollTimer: null,
      /** 各 tab 表格 max-height（px），表头固定、表体内部滚动 */
      tableMaxHeight: 600,
    };
  },

  computed: {
    ...mapState({
      pathOpts: (state) => state.pathList
    }),

    pathCacheKey() {
      if (!this.viewDate || !this.filterPathId) {
        return null;
      }
      return `${this.viewDate}_${this.filterPathId}`;
    },

    queryKey() {
      return `${this.viewDate || ""}_${this.filterPathId || ""}`;
    },

    solveStatus() {
      const summary = this.dayView && this.dayView.solveSummary;
      return (summary && summary.status) || "-";
    },

    areaStopWorkEv() {
      return (this.opAreaHourSnapshots || []).reduce((sum, row) => sum + (row.evObjective || 0), 0);
    },

    globalStopWorkEv() {
      const s = this.solveSummary;
      return s && s.stopWorkEv != null ? s.stopWorkEv : null;
    },

    penaltyValue() {
      const s = this.solveSummary;
      return s && s.penalty != null ? s.penalty : null;
    },

    slackPenaltyValue() {
      const s = this.solveSummary;
      return s && s.slackPenalty != null ? s.slackPenalty : null;
    },

    tripPenaltyValue() {
      const s = this.solveSummary;
      return s && s.tripPenalty != null ? s.tripPenalty : null;
    },

    repositionGainValue() {
      const s = this.solveSummary;
      return s && s.repositionGain != null ? s.repositionGain : null;
    },

    objectiveValue() {
      const s = this.solveSummary;
      return s && s.objective != null ? s.objective : null;
    },

    /** 收工EV − 各项惩罚，与页面展示口径一致 */
    reconciledObjectiveValue() {
      const stop = this.globalStopWorkEv;
      if (stop == null) {
        return this.objectiveValue;
      }
      return stop
        - (this.slackPenaltyValue || 0)
        - (this.penaltyValue || 0)
        - (this.tripPenaltyValue || 0)
        + (this.repositionGainValue || 0);
    },

    hasSolveMetrics() {
      return this.globalStopWorkEv != null
        || this.slackPenaltyValue != null
        || this.penaltyValue != null
        || this.tripPenaltyValue != null
        || this.objectiveValue != null;
    },

    solveExecutedAt() {
      const s = this.solveSummary;
      return s && s.executedAt ? s.executedAt : null;
    },

    solveOperator() {
      const s = this.solveSummary;
      return s && s.operator ? s.operator : null;
    },

    filteredUnassignedOrders() {
      const list = (this.dayView && this.dayView.unassignedOrders) || [];
      if (!this.filterPathId) return list;
      return list.filter((item) => item.pathId === this.filterPathId);
    },

    filteredDispatchedTrips() {
      const list = (this.dayView && this.dayView.dispatchedTrips) || [];
      if (!this.filterPathId) return list;
      return list.filter((item) => item.pathId === this.filterPathId);
    },

    filteredAssignedPerson() {
      return this.filteredDispatchedTrips.reduce((sum, trip) => sum + (trip.assignedSeats || 0), 0);
    },

    filteredUnassignedPerson() {
      return this.filteredUnassignedOrders.reduce((sum, item) => sum + (item.number || 0), 0);
    },

    filteredTripCount() {
      return this.filteredDispatchedTrips.length;
    },

    filterPathStartArea() {
      if (!this.filterPathId) {
        return null;
      }
      const path = (this.pathOpts || []).find((item) => item.id === this.filterPathId);
      return path && path.startArea != null ? path.startArea : null;
    },

    vehicleTipRows() {
      return this.filterVehicleTipsByPath(this.viewCache.vehicleTip[this.viewDate] || []);
    },

    startVehicleTipRows() {
      return this.filterVehicleTipsByPath(this.viewCache.startVehicleTip[this.viewDate] || []);
    },

    vehicleTipCount() {
      return this.vehicleTipRows.length;
    },

    startVehicleTipCount() {
      return this.startVehicleTipRows.length;
    },

    progressPercent() {
      if (!this.task || !this.task.totalDays) return 0;
      return Math.round((this.task.currentDay / this.task.totalDays) * 100);
    },

    progressText() {
      if (!this.task) return "";
      if (this.task.status === 0) {
        const day =
          this.task.dayResults && this.task.dayResults.length
            ? this.task.dayResults[this.task.dayResults.length - 1].date
            : this.param.startDate;
        return `推导中 第 ${this.task.currentDay}/${this.task.totalDays} 天 (${day})...`;
      }
      if (this.task.status === 1) return "推导完成";
      return `推导失败：${this.task.errorMsg || "未知错误"}`;
    }
  },

  watch: {
    viewDate() {
      this.handleQueryChange(false);
    },
    filterPathId() {
      this.handleQueryChange(false);
    }
  },

  mounted() {
    this.refreshAll();
  },

  beforeDestroy() {
    this.stopPoll();
  },

  methods: {
    formatEvNum(val) {
      if (val === null || val === undefined) {
        return "-";
      }
      return this.$options.filters.numFormat(val, 2);
    },

    normalizeOpAreaHourView(data) {
      if (Array.isArray(data)) {
        return { snapshots: data };
      }
      if (!data) {
        return { snapshots: [] };
      }
      return { snapshots: data.snapshots || [] };
    },

    isQueryCached() {
      if (!this.viewDate) {
        return false;
      }
      if (!this.viewCache.plan[this.viewDate]) {
        return false;
      }
      if (!Object.prototype.hasOwnProperty.call(this.viewCache.solveSummary, this.viewDate)) {
        return false;
      }
      if (!Object.prototype.hasOwnProperty.call(this.viewCache.vehicleTip, this.viewDate)) {
        return false;
      }
      if (!Object.prototype.hasOwnProperty.call(this.viewCache.startVehicleTip, this.viewDate)) {
        return false;
      }
      if (!this.filterPathId) {
        return true;
      }
      const key = this.pathCacheKey;
      return key && Object.prototype.hasOwnProperty.call(this.viewCache.sellable, key)
        && Object.prototype.hasOwnProperty.call(this.viewCache.opAreaHour, key);
    },

    handleQueryChange(forcePathRefresh) {
      this.syncFromCache();
      const key = this.queryKey;
      if (!forcePathRefresh && key === this.lastLoadedQueryKey) {
        return;
      }
      if (!forcePathRefresh && this.isQueryCached()) {
        this.lastLoadedQueryKey = key;
        return;
      }
      if (!this.viewDate) {
        return;
      }
      this.refreshAll();
    },

    invalidatePathViewCache() {
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.lastLoadedQueryKey = null;
    },

    filterVehicleTipsByPath(rows) {
      const list = rows || [];
      if (!this.filterPathId) {
        return list;
      }
      const startArea = this.filterPathStartArea;
      if (startArea == null) {
        return [];
      }
      return list.filter((row) => row && row.areaId === startArea);
    },

    syncFromCache() {
      this.dayView = this.viewCache.plan[this.viewDate] || null;
      this.solveSummary = this.viewCache.solveSummary[this.viewDate] || null;
      const key = this.pathCacheKey;
      this.sellableSnapshots = key ? (this.viewCache.sellable[key] || []) : [];
      const opCached = key ? this.viewCache.opAreaHour[key] : null;
      this.opAreaHourSnapshots = this.normalizeOpAreaHourView(opCached).snapshots;
    },

    onRefresh() {
      if (!this.viewDate) {
        this.$message.warning("请选择日期");
        return;
      }
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.$delete(this.viewCache.vehicleTip, this.viewDate);
      this.$delete(this.viewCache.startVehicleTip, this.viewDate);
      this.$delete(this.viewCache.solveSummary, this.viewDate);
      this.lastLoadedQueryKey = null;
      this.refreshAll();
    },

    refreshAll() {
      if (!this.viewDate) {
        return Promise.resolve();
      }
      this.refreshing = true;
      const planReq = getEvPlanView({ date: this.viewDate, _hideLoading: true });
      const summaryReq = getEvSolveSummaryView({ date: this.viewDate, _hideLoading: true });
      const vehicleTipReq = getEvVehicleTipView({ date: this.viewDate, _hideLoading: true });
      const startVehicleTipReq = getEvStartVehicleTipView({ date: this.viewDate, _hideLoading: true });
      const pathKey = this.pathCacheKey;
      const pathReqs = pathKey
        ? [
          getEvSellableView({ date: this.viewDate, pathId: this.filterPathId, _hideLoading: true }),
          getEvOpAreaHourView({ date: this.viewDate, pathId: this.filterPathId, _hideLoading: true })
        ]
        : [];

      return Promise.all([planReq, summaryReq, vehicleTipReq, startVehicleTipReq, ...pathReqs])
        .then(([planRes, summaryRes, vehicleTipRes, startVehicleTipRes, ...pathResults]) => {
          this.$set(this.viewCache.plan, this.viewDate, planRes.data);
          this.dayView = planRes.data;
          const summary = summaryRes.data || null;
          this.$set(this.viewCache.solveSummary, this.viewDate, summary);
          this.solveSummary = summary;
          this.$set(this.viewCache.vehicleTip, this.viewDate, vehicleTipRes.data || []);
          this.$set(this.viewCache.startVehicleTip, this.viewDate, startVehicleTipRes.data || []);
          if (!pathKey) {
            this.sellableSnapshots = [];
            this.opAreaHourSnapshots = [];
            return;
          }
          const [sellableRes, opAreaHourRes] = pathResults;
          const sellable = sellableRes.data || [];
          const opView = this.normalizeOpAreaHourView(opAreaHourRes.data);
          this.$set(this.viewCache.sellable, pathKey, sellable);
          this.$set(this.viewCache.opAreaHour, pathKey, opView.snapshots);
          this.sellableSnapshots = sellable;
          this.opAreaHourSnapshots = opView.snapshots;
        })
        .finally(() => {
          this.refreshing = false;
          this.lastLoadedQueryKey = this.queryKey;
        });
    },

    startSimulate() {
      if (!this.param.startDate) {
        this.$message.error("请选择开始推导日期");
        return;
      }
      this.stopPoll();
      this.taskId = null;
      this.task = null;
      this.running = true;
      this.getResulted = false;

      submitEvSimulate({
        startDate: this.param.startDate,
        days: this.param.days
      })
        .then((res) => {
          this.getResulted = true;
          this.taskId = res.data.taskId;
          this.pollTask();
          this.pollTimer = setInterval(() => this.pollTask(), 5000);
        })
        .catch(() => {
          this.running = false;
          this.getResulted = true;
        });
    },

    pollTask() {
      if (!this.taskId || !this.getResulted) return;

      getEvSimulateTask(this.taskId).then((res) => {
        this.task = res.data;
        if (this.task.status === 1) {
          this.running = false;
          this.stopPoll();
          this.viewDate = this.param.startDate;
          this.viewCache.opAreaHour = {};
          this.viewCache.sellable = {};
          this.viewCache.vehicleTip = {};
          this.viewCache.startVehicleTip = {};
          this.viewCache.solveSummary = {};
          this.lastLoadedQueryKey = null;
          this.refreshAll();
          this.$message.success("推导完成，数据已刷新");
        } else if (this.task.status === 2) {
          this.running = false;
          this.stopPoll();
        }
      });
    },

    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    goConfig() {
      this.$router.push("/work_common/ev_simulate/config");
    }
  }
};
</script>

<style lang="less" scoped>
.ev-simulate-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px);
  padding: 6px 12px 8px;
  box-sizing: border-box;
  overflow: hidden;
}

.page-header {
  flex-shrink: 0;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebeef5;
}

.header-main {
  display: flex;
  align-items: stretch;
  gap: 12px;
  min-height: 36px;

  /deep/ .el-tooltip {
    flex: 1;
    min-width: 0;
    display: flex;
  }
}

.header-block {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.block-label {
  font-size: 11px;
  color: #909399;
  writing-mode: vertical-rl;
  letter-spacing: 2px;
  margin-right: 8px;
  flex-shrink: 0;
  user-select: none;
}

.derive-block,
.view-block {
  padding: 4px 10px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.ev-block {
  flex: 1;
  width: 100%;
  justify-content: center;
  gap: 4px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #f0f7ff 0%, #fafcff 100%);
  border: 1px solid #d9ecff;
  border-radius: 4px;
  min-width: 0;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;

  &::-webkit-scrollbar {
    height: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c0c4cc;
    border-radius: 2px;
  }
}

.ev-placeholder {
  background: #fafafa;
  border: 1px dashed #dcdfe6;
}

.ev-placeholder-text {
  font-size: 12px;
  color: #c0c4cc;
  width: 100%;
  text-align: center;
}

.ev-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding: 0 6px;
  border-right: 1px solid #e4e7ed;

  &:last-child {
    border-right: none;
  }
}

.ev-metric-label {
  font-size: 11px;
  color: #909399;
  white-space: nowrap;
  line-height: 1.4;
}

.ev-metric-value {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;

  &.muted {
    color: #606266;
    font-size: 13px;
  }
}

.ev-metric.is-penalty .ev-metric-value {
  color: #f56c6c;
}

.ev-metric.is-objective .ev-metric-value {
  color: #409eff;
  font-size: 14px;
}

.header-sub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  min-height: 22px;
  flex-wrap: wrap;
  gap: 8px;
}

.plan-stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.stat-tag {
  font-size: 12px;
  color: #606266;
  background: #f0f2f5;
  padding: 2px 8px;
  border-radius: 2px;
  white-space: nowrap;
}

.stat-tag--time {
  color: #409eff;
  background: #ecf5ff;
}

.task-status {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.toolbar__progress {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 160px;
}

.inline-progress {
  width: 120px;
  flex-shrink: 0;
}

.progress-pct {
  font-size: 12px;
  color: #606266;
  min-width: 32px;
}

.status-text {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.toolbar__done {
  font-size: 12px;
  color: #67c23a;
  white-space: nowrap;
}

.toolbar__error {
  font-size: 12px;
  color: #f56c6c;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compact-form {
  /deep/ .el-form-item {
    margin-bottom: 0;
    margin-right: 8px;
  }

  /deep/ .el-form-item__label {
    padding-right: 4px;
    font-size: 12px;
  }
}

.derive-block,
.view-block {
  /deep/ .el-form {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
  }
}

.main-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.fill-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /deep/ .el-tabs__header {
    margin-bottom: 6px;
  }

  /deep/ .el-tabs__content {
    flex: 1;
    min-height: 0;
  }

  /deep/ .el-tab-pane {
    height: 100%;
    overflow-y: auto;
  }
}

.idle-empty-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
  font-size: 13px;
}
</style>
